import { mockEhrGroupIds } from "@/configuration/ehr/mock";
import { GroupIds } from "@/configuration/group-ids";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { isAthenaEhrGroup } from "@/src/lib/ehr/athena/athena-constants";
import { isEhrMockEnabled } from "@/src/lib/ehr/mock-ehr-mode";
import { Group } from "@/src/models/group";

export const VALERIE_GROUP_NAMES = ["Valerie Health", "Valerie Health - Training", "Valerie Health (DEPRECATED)"];

const VALERIE_MOCK_CONFIG_SUBDOMAINS = new Set<string>([
  ConfigSubdomain.VALERIE,
  ConfigSubdomain.VALERIE_HEALTH_TRAINING,
]);

// Group-id allowlist for the Supabase mock EHR — takes precedence over the subdomain set below.
// Used to point individual sandbox groups at the mock without moving their shared production
// subdomains there. getEhrIntegration checks isValerieMockEhrGroup first, so this wins over the
// group's real EHR branch while production is unaffected.
// Mirrors mockSchedulingGroupIds in scheduling-integration.ts (VAL-1882).
const VALERIE_MOCK_EHR_GROUP_IDS = new Set<string>([
  ...mockEhrGroupIds,
  GroupIds.ADSS_LIVE_SANDBOX,
  GroupIds.ENT_PARTNERS_LIVE_SANDBOX,
  GroupIds.ENT_PARTNERS_SCI_LIVE_SANDBOX,
  GroupIds.NIMBUS_LIVE_SANDBOX,
  GroupIds.PA_CENTRAL_REGION_LIVE_SANDBOX,
  GroupIds.PMG_LIVE_SANDBOX,
  GroupIds.TPR_BILTMORE_LIVE_SANDBOX,
  GroupIds.SPORTORTHO_LIVE_SANDBOX,
  GroupIds.UPPERLINE_GEORGIA_LIVE_SANDBOX,
  GroupIds.USHV_HEARTPLACE_LIVE_SANDBOX,
  GroupIds.USHV_MKC_LIVE_SANDBOX,
  GroupIds.AXIA_LIVE_SANDBOX,
]);

const ECW_SYSTEM_CONFIG_SUBDOMAINS = new Set<string>([
  ConfigSubdomain.PMG,
  ConfigSubdomain.PINNACLE,
  ConfigSubdomain.NIMBUS,
  ConfigSubdomain.AUTONOMY,
  ConfigSubdomain.ENT_PARTNERS,
  ConfigSubdomain.ENT_PARTNERS_SCI,
  ConfigSubdomain.ENT_PARTNERS_SUBOTO,
  ConfigSubdomain.MASS_ENT,
  ConfigSubdomain.USHV_HEARTPLACE,
  ConfigSubdomain.PA_EAST_REGION,
  ConfigSubdomain.PA_CENTRAL_REGION,
  ConfigSubdomain.USHV_HEARTLAND,
  ConfigSubdomain.USHV_CCOH,
  ConfigSubdomain.USHV_FORTWORTH,
  ConfigSubdomain.USHV_MCA,
  ConfigSubdomain.USHV_MKC,
  ConfigSubdomain.USHV_ORION,
  ConfigSubdomain.USHV_PIMA,
]);

/** Whether this group's EHR calls go to the Supabase mock: always under `MOCK_EHR`, else by allowlist. */
export function isValerieMockEhrGroup(group: Group): boolean {
  return (
    isEhrMockEnabled() ||
    VALERIE_MOCK_EHR_GROUP_IDS.has(group.id) ||
    VALERIE_MOCK_CONFIG_SUBDOMAINS.has(getGroupConfigSubdomain(group))
  );
}

export function usesEcwSystemType(group: Group): boolean {
  return ECW_SYSTEM_CONFIG_SUBDOMAINS.has(getGroupConfigSubdomain(group));
}

export enum EhrSystemType {
  ADVANCED_MD = "AdvancedMD",
  ECW = "eCW",
  ATHENA = "Athena",
  MOD_MED = "ModMed",
  SALESFORCE = "Salesforce",
}

export function getEhrSystemType(group: Group): EhrSystemType | null {
  if (getGroupConfigSubdomain(group) === ConfigSubdomain.ADSS) {
    return EhrSystemType.ADVANCED_MD;
  }

  if (isAthenaEhrGroup(group)) {
    return EhrSystemType.ATHENA;
  }

  if (usesEcwSystemType(group)) {
    return EhrSystemType.ECW;
  }

  if (getGroupConfigSubdomain(group) === ConfigSubdomain.COUG) {
    return EhrSystemType.MOD_MED;
  }

  if (getGroupConfigSubdomain(group) === ConfigSubdomain.BALANCE_HEALTH) {
    return EhrSystemType.MOD_MED;
  }

  if (getGroupConfigSubdomain(group) === ConfigSubdomain.UVVC) {
    return EhrSystemType.SALESFORCE;
  }

  return null;
}
